import { useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Sparkles, Loader2, RotateCw, AtSign, History, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Chat() {
  const { user, workspace, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [people, setPeople] = useState([]);
  const [grantedIds, setGrantedIds] = useState(new Set());
  const [pendingIds, setPendingIds] = useState(new Set());
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIdx, setMentionIdx] = useState(0);
  const [shadowInfo, setShadowInfo] = useState(null);
  const endRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);
  useEffect(() => () => abortRef.current?.abort(), []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/memory/people");
        setPeople(data);
      } catch { /* silent */ }
      // Bot access — mark which peers are queryable
      try {
        const { data } = await api.get("/bot-access/mine");
        setGrantedIds(new Set((data.held || []).map((g) => g.target_user_id)));
        setPendingIds(new Set((data.pending_outgoing || []).map((g) => g.target_user_id)));
      } catch { /* silent */ }
      // Shadow mode banner — active induction with shadow ON.
      try {
        const { data } = await api.get("/succession/mine");
        if (data?.active && data.succession?.shadow_mode) {
          setShadowInfo({ name: data.succession.predecessor?.name || "predecessor" });
        }
      } catch { /* silent */ }
    })();
    // Prefill from ?prefill= (used by Memory page's "Ask AI about them" link + Induction buddy).
    const pre = params.get("prefill");
    if (pre) {
      setQ(pre);
      const next = new URLSearchParams(params);
      next.delete("prefill");
      setParams(next, { replace: true });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    // eslint-disable-next-line
  }, []);

  // /mention autocomplete: only trigger when the whole input starts with `/token`
  const mentionMatches = useMemo(() => {
    if (!showMentions) return [];
    const needle = mentionQuery.toLowerCase();
    return people
      .filter((p) => {
        if (!needle) return true;
        const bag = [p.name, p.email, ...(p.aliases || [])].filter(Boolean).map((s) => s.toLowerCase());
        return bag.some((s) => s.includes(needle));
      })
      .slice(0, 6);
  }, [people, mentionQuery, showMentions]);

  const canQueryPerson = (p) => {
    if (!p) return false;
    if (p.user_id === user?.id) return true;
    if (workspace?.open_bot_access) return true;
    if (isAdmin) return true;
    return grantedIds.has(p.user_id);
  };

  const requestAccess = async (p) => {
    try {
      const r = await api.post("/bot-access/request", { target_user_id: p.user_id });
      if (r.data.already) toast.info("Request already pending");
      else toast.success(`Request sent to ${p.name || p.email}`);
      setPendingIds((s) => new Set([...s, p.user_id]));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send request");
    }
    setShowMentions(false);
  };

  const applyMention = (person) => {
    if (!canQueryPerson(person)) { requestAccess(person); return; }
    const alias = person.aliases?.[0] || person.name?.split(" ")[0].toLowerCase() || person.email.split("@")[0];
    // Preserve anything the user typed after the /token.
    const m = q.match(/^\s*\/[A-Za-z0-9._\-]*(.*)$/s);
    const rest = (m && m[1]) || "";
    setQ(`/${alias}${rest.startsWith(" ") ? rest : " " + rest.trimStart()}`);
    setShowMentions(false);
    setMentionIdx(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onQChange = (e) => {
    const v = e.target.value;
    setQ(v);
    const m = v.match(/^\s*\/([A-Za-z0-9._\-]*)$/);
    if (m) { setShowMentions(true); setMentionQuery(m[1]); setMentionIdx(0); }
    else if (v.startsWith("/") && !v.includes(" ")) { setShowMentions(true); setMentionQuery(v.slice(1)); }
    else setShowMentions(false);
  };

  const onKeyDown = (e) => {
    if (!showMentions || mentionMatches.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx((i) => (i + 1) % mentionMatches.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setMentionIdx((i) => (i - 1 + mentionMatches.length) % mentionMatches.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); applyMention(mentionMatches[mentionIdx]); }
    else if (e.key === "Escape") { setShowMentions(false); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!q.trim() || busy) return;
    setShowMentions(false);
    const question = q.trim();
    setQ("");
    await runStream(question);
  };

  const runStream = async (question) => {
    // Stable id so we can update by identity, immune to array index shifts.
    const assistantId = crypto.randomUUID();
    setMessages((m) => [
      ...m,
      { role: "user", content: question },
      { role: "assistant", id: assistantId, content: "", citations: [], streaming: true, question },
    ]);
    setBusy(true);

    const updateAssistant = (patch) => {
      setMessages((m) => m.map((msg) => (msg.id === assistantId ? { ...msg, ...patch } : msg)));
    };
    const appendContent = (chunk) => {
      setMessages((m) => m.map((msg) => (msg.id === assistantId ? { ...msg, content: (msg.content || "") + chunk } : msg)));
    };
    const appendShadow = (chunk) => {
      setMessages((m) => m.map((msg) => (msg.id === assistantId ? { ...msg, shadowContent: (msg.shadowContent || "") + chunk } : msg)));
    };

    const controller = new AbortController();
    abortRef.current = controller;
    const token = localStorage.getItem("cb_token");

    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ message: question, session_id: sessionId }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        // Normalize CRLF -> LF in case any proxy rewrites line endings
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

        // Parse complete SSE frames (double-newline delimited)
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const line = frame.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          let payload;
          try { payload = JSON.parse(line.slice(5).trim()); } catch { continue; }

          if (payload.type === "meta") {
            setSessionId(payload.session_id);
            updateAssistant({
              citations: payload.citations || [],
              confidence: payload.confidence || null,
              id: assistantId, backendId: payload.id,
            });
          } else if (payload.type === "delta") {
            appendContent(payload.content);
          } else if (payload.type === "shadow_meta") {
            updateAssistant({
              shadow: { predecessor: payload.predecessor, citations: payload.citations || [] },
              shadowContent: "",
              shadowStreaming: true,
            });
          } else if (payload.type === "shadow_delta") {
            appendShadow(payload.content);
          } else if (payload.type === "shadow_error") {
            updateAssistant({ shadowError: payload.detail, shadowStreaming: false });
          } else if (payload.type === "error") {
            updateAssistant({
              error: true,
              retryable: !!payload.retryable,
              errorDetail: payload.detail,
            });
          } else if (payload.type === "done") {
            updateAssistant({ streaming: false, shadowStreaming: false });
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        updateAssistant({
          content: "",
          error: true,
          retryable: true,
          errorDetail: "Connection lost. Please retry.",
          streaming: false,
          shadowStreaming: false,
        });
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  const retry = async (msgId) => {
    if (busy) return;
    setBusy(true);  // prevent double-click; runStream will also set/keep it
    let question = "";
    setMessages((m) => {
      const target = m.find((x) => x.id === msgId);
      question = target?.question || "";
      const idx = m.findIndex((x) => x.id === msgId);
      if (idx < 0) return m;
      return m.slice(0, Math.max(0, idx - 1));
    });
    setTimeout(() => {
      if (question) {
        runStream(question);
      } else {
        setBusy(false);
      }
    }, 0);
  };

  const suggestions = [
    "How do we handle refunds?",
    "What's our pricing exception policy?",
    "Who owns customer onboarding?",
    "Summarize our support SLAs",
  ];

  return (
    <div className="flex flex-col h-full">
      <header className="p-6 border-b border-border/60 flex items-start justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Ask the brain</p>
          <h1 className="font-display text-2xl">Sourced answers, streamed live</h1>
        </div>
        {shadowInfo && (
          <Link
            to="/app/induction"
            data-testid="chat-shadow-banner"
            className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-sky-600 dark:text-sky-400 border border-sky-500/30 bg-sky-500/5 rounded-full px-3 py-1.5 hover:bg-sky-500/10 transition-colors shrink-0"
          >
            <History className="w-3 h-3" /> Shadow mode · {shadowInfo.name}'s memory active
          </Link>
        )}
      </header>

      <div className="flex-1 overflow-auto p-6" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto text-center py-12" data-testid="chat-empty-state">
            <div className="empty-icon w-14 h-14 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 flex items-center justify-center mx-auto mb-5 text-fuchsia-500">
              <Sparkles className="w-6 h-6 text-fuchsia-500" />
            </div>
            <p className="font-display text-2xl mb-2">What do you want to know?</p>
            <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
              Sourced answers, streamed live from your workspace knowledge. Type <code className="text-xs bg-secondary/60 px-1.5 py-0.5 rounded">/</code> to query a teammate's memory.
            </p>
            <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-3">Try one of these</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg mx-auto">
              {suggestions.map((s, idx) => (
                <button
                  key={s}
                  onClick={() => setQ(s)}
                  data-testid={`chat-suggestion-${idx}`}
                  className="text-left text-sm p-3 rounded-md border border-border/60 hover:border-foreground/40 hover:bg-secondary/40 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto space-y-6">
          {messages.map((m, i) => (
            <div key={i} data-testid={`chat-msg-${i}`}>
              {m.role === "user" ? (
                <div className="flex justify-end">
                  <div className="bg-foreground text-background rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[75%]">
                    <p className="text-sm">{m.content}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Brain
                    {m.streaming && <Loader2 className="w-3 h-3 animate-spin" data-testid="chat-streaming-indicator" />}
                    {m.confidence && !m.streaming && (
                      <span
                        data-testid={`chat-confidence-${i}`}
                        title={m.confidence.reason || ""}
                        className={`ml-2 inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                          m.confidence.label === "high"
                            ? "text-emerald-500 border-emerald-500/40 bg-emerald-500/10"
                            : m.confidence.label === "medium"
                            ? "text-amber-500 border-amber-500/40 bg-amber-500/10"
                            : m.confidence.label === "low"
                            ? "text-red-500 border-red-500/40 bg-red-500/10"
                            : "text-muted-foreground border-border/60"
                        }`}
                      >
                        {m.confidence.label} · {Math.round(m.confidence.score * 100)}%
                      </span>
                    )}
                  </div>
                  {m.citations?.length > 0 && (
                    <div className="pb-2 border-b border-border/40">
                      <p className="eyebrow mb-2">Sources</p>
                      <div className="flex flex-wrap gap-2">
                        {m.citations.map((c) => (
                          <span key={c.index} className="inline-flex items-center gap-1 text-xs border border-border/60 rounded-full px-2.5 py-1"
                                data-testid={`citation-${c.index}`}>
                            <span className="font-mono text-muted-foreground">#{c.index}</span>
                            <span>{c.title}</span>
                            <span className="text-muted-foreground">· {c.department}</span>
                            {c.file_refs && c.file_refs.length > 0 && (
                              <span title={`Mentioned files: ${c.file_refs.join(", ")}. Ask the owner or check shared storage.`}
                                    className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 border border-amber-500/40"
                                    data-testid={`citation-files-${c.index}`}>
                                📎 {c.file_refs.length}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="markdown-body text-sm leading-relaxed" data-testid={`chat-answer-${i}`}>
                    {m.content ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    ) : m.streaming ? (
                      <span className="text-muted-foreground italic">…thinking</span>
                    ) : null}
                    {m.streaming && m.content && <span className="inline-block w-1.5 h-3.5 bg-foreground/60 ml-0.5 animate-pulse" />}
                  </div>

                  {/* Shadow answer — predecessor's preserved memory (induction mode) */}
                  {(m.shadow || m.shadowError) && (
                    <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-4" data-testid={`chat-shadow-${i}`}>
                      <div className="text-xs uppercase tracking-wider text-sky-600 dark:text-sky-400 flex items-center gap-1.5 mb-2">
                        <History className="w-3 h-3" />
                        Shadow · {m.shadow?.predecessor?.name || "Predecessor"}'s preserved memory
                        {m.shadowStreaming && <Loader2 className="w-3 h-3 animate-spin" data-testid={`chat-shadow-streaming-${i}`} />}
                      </div>
                      {m.shadow?.citations?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {m.shadow.citations.map((c) => (
                            <span key={c.index} className="inline-flex items-center gap-1 text-[11px] border border-sky-500/30 rounded-full px-2.5 py-0.5 text-muted-foreground">
                              <span className="font-mono">#{c.index}</span>
                              <span>{c.title}</span>
                            </span>
                          ))}
                        </div>
                      )}
                      {m.shadowError ? (
                        <p className="text-xs text-muted-foreground italic">{m.shadowError}</p>
                      ) : (
                        <div className="markdown-body text-sm leading-relaxed" data-testid={`chat-shadow-answer-${i}`}>
                          {m.shadowContent ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.shadowContent}</ReactMarkdown>
                          ) : (
                            <span className="text-muted-foreground italic">…consulting preserved memory</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {m.error && (
                    <div
                      className="flex items-center justify-between gap-3 p-3 rounded-md border border-destructive/30 bg-destructive/5 text-sm"
                      data-testid={`chat-error-${i}`}
                    >
                      <span className="text-destructive">{m.errorDetail || "Something went wrong."}</span>
                      {m.retryable && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full shrink-0"
                          onClick={() => retry(m.id)}
                          disabled={busy}
                          data-testid={`chat-retry-btn-${i}`}
                        >
                          <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Retry
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>
      </div>

      <form onSubmit={submit} className="p-4 border-t border-border/60">
        <div className="max-w-3xl mx-auto relative">
          {showMentions && mentionMatches.length > 0 && (
            <div className="absolute bottom-full mb-2 left-0 right-14 rounded-lg border border-border/60 bg-popover shadow-lg overflow-hidden z-30" data-testid="chat-mentions-popover">
              <div className="px-3 py-2 border-b border-border/60 text-[10px] uppercase tracking-wider text-muted-foreground font-mono flex items-center gap-1.5">
                <AtSign className="w-3 h-3" /> Ask a person's memory
              </div>
              <ul>
                {mentionMatches.map((p, i) => {
                  const locked = !canQueryPerson(p);
                  const pending = pendingIds.has(p.user_id);
                  return (
                  <li key={p.user_id}>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); applyMention(p); }}
                      onMouseEnter={() => setMentionIdx(i)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2.5 ${i === mentionIdx ? "bg-secondary" : "hover:bg-secondary/50"} ${locked ? "opacity-70" : ""}`}
                      data-testid={`chat-mention-option-${p.user_id}`}
                      title={locked ? (pending ? "Access request pending" : "Click to request access") : ""}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 ${locked ? "bg-muted text-muted-foreground" : "bg-foreground text-background"}`}>
                        {(p.name || p.email || "?")[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate flex items-center gap-1.5">
                          {p.name || p.email}
                          {locked && (
                            <Lock className="w-3 h-3 text-muted-foreground" data-testid={`chat-mention-locked-${p.user_id}`} />
                          )}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono truncate">
                          /{p.aliases?.[0] || "—"} · {p.memory_count} entr{p.memory_count === 1 ? "y" : "ies"}
                          {locked && (pending ? " · request pending" : " · click to request")}
                        </p>
                      </div>
                    </button>
                  </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={q}
              onChange={onQChange}
              onKeyDown={onKeyDown}
              placeholder="Ask about processes, policies, decisions… or type / to query a teammate's memory"
              className="flex-1"
              disabled={busy}
              data-testid="chat-input"
              autoComplete="off"
            />
            <Button type="submit" disabled={busy || !q.trim()} className="rounded-full" data-testid="chat-submit-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
